import { BookOpen, Sparkles, DoorOpen, Handshake, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Link } from "react-router-dom";

const features = [
  {
    icon: BookOpen,
    title: "Education",
    description: "Building a knowledge hub for DePIN through content, workshops, and interactive discussion.",
  },
  {
    icon: Sparkles,
    title: "Empowerment",
    description: "Creating opportunities for ownership, income, and technological advancement.",
  },
  {
    icon: DoorOpen,
    title: "Lower Entry Barrier",
    description: "User friendly tools and guides that reduce technical complexity.",
  },
  {
    icon: Handshake,
    title: "Strategic partnership",
    description: "Collaborations with projects and institutions accelerating DePIN adoptions across Africa.",
  },
];

const AboutUs = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section 
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="about-us" 
      className="py-8 md:py-12 relative overflow-hidden bg-background"
    >
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,hsl(var(--muted-foreground)/0.08)_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div id="about-hero" className="container mx-auto px-4 md:px-6 relative z-10">
        {/* About Section - Clean Left/Right Layout with slide-in from left */}
        <div className={`mb-16 transition-all duration-1000 ease-out ${isVisible ? "translate-x-0 opacity-100 blur-0" : "-translate-x-12 opacity-0 blur-sm"}`}>
          <div className="grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-border/50">
            {/* Left Content Side */}
            <div className="relative p-8 md:p-12 lg:p-16 bg-gradient-to-br from-card via-card to-muted/30">
              {/* Decorative accent */}

              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />

              <p className="text-sm font-mono tracking-widest uppercase mb-4 text-muted-foreground">Who we are</p>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
                Discover <span className="text-muted-foreground">DePIN</span>{" "}
                <span className="text-muted-foreground">Land</span>
              </h2>

              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              A web3 platform focused on educating, onboarding and enabling real world adoption of decentralized physical Infrastructure networks (DePIN) in Africa. 
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/about" aria-label="Learn more about DePIN Land and our mission">
                  <Button size="lg" className="gap-2 group bg-foreground text-background hover:bg-foreground/90">
                    Learn More
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Design Side */}
            <div className="relative p-8 md:p-12 lg:p-16 bg-gradient-to-bl from-primary/10 via-muted/50 to-card flex items-center justify-center min-h-[300px] lg:min-h-0">
              {/* Decorative Pattern */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,hsl(var(--primary)/0.15)_1px,transparent_1px)] bg-[size:24px_24px]" />

              {/* Floating geometric shapes */}
              <div className="absolute top-8 right-8 w-20 h-20 border-2 border-primary/30 rounded-2xl rotate-12" />
              <div className="absolute bottom-12 left-8 w-16 h-16 bg-primary/20 rounded-full blur-sm" />
              <div className="absolute top-1/2 right-1/4 w-12 h-12 border border-primary/20 rounded-lg -rotate-6" />

              {/* Central decorative element */}
              <div className="relative z-10 flex flex-col items-center justify-center text-center">
                <div className="w-24 h-24 mb-6 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/20 flex items-center justify-center backdrop-blur-sm">
                  <div className="w-12 h-12 rounded-xl bg-primary/40 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-lg bg-primary" />
                  </div>
                </div>
                <p className="text-sm font-mono text-primary/80 tracking-widest uppercase">Building the Future</p>
                <p className="text-xs text-muted-foreground mt-2">Decentralized Infrastructure</p>
              </div>

              {/* Corner accents */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/20 to-transparent" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/10 to-transparent" />
            </div>
          </div>
        </div>

        {/* Features Grid - About page styling */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 scale-100 opacity-100 blur-0" : "translate-y-10 scale-95 opacity-0 blur-sm"}`}
              style={{ transitionDelay: isVisible ? `${400 + index * 100}ms` : "0ms" }}
            >
              <div className="group relative p-6 h-full bg-card border border-border rounded-2xl hover:bg-muted/30 transition-colors">
                  {/* Icon */}
                  <div className="p-3 rounded-xl bg-muted/50 group-hover:bg-background transition-colors w-fit mb-4">
                    <feature.icon className="h-6 w-6 text-foreground" strokeWidth={1.5} />
                  </div>

                  {/* Content */}
                <h3 className="text-lg font-semibold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
export default AboutUs;
